import { MsalProvider } from '@azure/msal-react';
import {
  AccessToken,
  AuthRedirect,
  AxiosConfiguration,
  msalConfig,
} from '@dispatch/web/auth';
import {
  ClientCache,
  InvalidPermissions,
  NotFound,
  NotificationProvider,
  snackNotifications,
  ErrorBoundary,
} from '@dispatch/web/ui';
import { LicenseInfo } from '@mui/x-license';
import { ReactNode, StrictMode } from 'react';
import * as ReactDOM from 'react-dom';
import {
  Navigate,
  Route,
  Routes,
  unstable_HistoryRouter as BrowserRouter,
} from 'react-router-dom';
import App from './app/App';
import LoginPage from './pages/login/LoginPage';
import AcceptInvitePage from './pages/accept-invite/AcceptInvitePage';
import SignupRedirectPage from './pages/signup-redirect/SignupRedirectPage';
import Resources from './pages/resources/list/ResourcesPage';
import TaskGroupCreatePage from './pages/task-groups/create/TaskGroupCreatePage';
import TaskGroupDetailsPage from './pages/task-groups/details/TaskGroupDetailsPage';
import TaskGroupsPage from './pages/task-groups/list/TaskGroupsPage';
import CreateTaskTemplate from './pages/task-templates/create/CreateTaskTemplate';
import TaskTemplateDetailsPage from './pages/task-templates/details/TaskTemplateDetailsPage';
import TaskTemplatesPage from './pages/task-templates/list/TaskTemplatesPage';
import ResourceDetailsPage from './pages/resources/details/ResourceDetailsPage';
import RegionsPage from './pages/regions/list/RegionsPage';
import RegionsDetailsPage from './pages/regions/details/RegionDetailsPage';
import RegionsCreatePage from './pages/regions/create/RegionCreatePage';
import { TasksListPage } from './pages/tasks/TasksListPage';
import { UsersPage } from './pages/users/list/UsersPage';
import { UserDetailsPage } from './pages/users/details/UserDetailsPage';
import { UserCreatePage } from './pages/users/create/UserCreatePage';
import { PublicClientApplication } from '@azure/msal-browser';
import RolesPage from './pages/roles/list/RolesPage';
import RoleDetailsPage from './pages/roles/details/RoleDetailsPage';
import RoleCreatePage from './pages/roles/create/RoleCreatePage';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  FilterProvider,
  CanNavigateToPage,
  PermissionProvider,
  TableProvider,
  CurrentUserResourceProvider,
  CompanyProvider,
  ExportNotificationProvider,
} from '@dispatch/shared/hooks';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { CssBaseline } from '@mui/material';
import { ThemeModeProvider } from '@dispatch/web/theme';
import { TaskDetailsPage } from './pages/tasks/details/TaskDetailsPage';
import { SchedulerViewPage } from './pages/scheduler/SchedulerViewPage';
import { TelemetryProvider, trackError } from '@dispatch/web/telemetry';
import { createBrowserHistory } from 'history';
import { ResourceCreatePage } from './pages/resources/create/ResourceCreatePage';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ComplexLossPage } from './pages/complex-loss/ComplexLossPage';
import { ComplexLossDetailsPage } from './pages/complex-loss/details/ComplexLossDetailsPage';
import { CompanyAdminPage } from './pages/company-admin/CompanyAdminPage';
import { CapacityDashboardPage } from './pages/capacity-admin/list/CapacityDashboardPage';
import { BulkUpdateResourcePage } from './pages/resources/list/BulkUpdateResourcePage';
import { TaskCreatePage } from './pages/tasks/taskCreate/TaskCreatePage';
import { BulkTaskCancellation } from './pages/tasks/BulkCancellation/BulkTaskCancellation';
import { BulkTaskLock } from './pages/tasks/BulkLock/BulkTaskLock';
import { GeneralDataContextProvider } from '@dispatch/shared/hooks';
import { ReportsPage } from './pages/Export/ReportsPage';
import { QualifiedResourcesTaskCreatePage } from './pages/tasks/taskCreate/QualifiedResourcesTaskCreationPage';

LicenseInfo.setLicenseKey(
  'd4fa41be8d7c6184600a0bf8275e640bTz0xMTk4MzcsRT0xNzkwODk5MTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
);

const customHistory = createBrowserHistory();
export const msalInstance = new PublicClientApplication(msalConfig);

const queryClient = new QueryClient(ClientCache);

const AppProviders = ({ children }: { children: ReactNode }) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log to Application Insights and console
    trackError(error, {
      boundaryName: 'Application',
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <ErrorBoundary
      boundaryName="Application"
      showDetails={false}
      onError={handleError}
    >
      <TelemetryProvider>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeModeProvider>
                <CssBaseline />
                <FilterProvider>
                  <AccessToken>
                    <AxiosConfiguration>
                      <PermissionProvider>
                        <CurrentUserResourceProvider>
                          <TableProvider>
                            <CompanyProvider>
                              <GeneralDataContextProvider>
                                <NotificationProvider>
                                  {children}
                                </NotificationProvider>
                              </GeneralDataContextProvider>
                            </CompanyProvider>
                          </TableProvider>
                        </CurrentUserResourceProvider>
                      </PermissionProvider>
                    </AxiosConfiguration>
                  </AccessToken>
                </FilterProvider>
              </ThemeModeProvider>
            </LocalizationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </MsalProvider>
      </TelemetryProvider>
    </ErrorBoundary>
  );
};
/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
ReactDOM.render(
  <StrictMode>
    <AppProviders>
      <BrowserRouter history={customHistory}>
        <ExportNotificationProvider>
          <Routes>
            <Route path="/sign-in" element={<LoginPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/signup-redirect" element={<SignupRedirectPage />} />
            <Route path="/" element={<App />}>
              <Route path="/auth-redirect" element={<AuthRedirect />} />
              <Route
                path="/invalid-permissions"
                element={<InvalidPermissions />}
              />
              <Route path="*" element={<NotFound />} />
              <Route path="" element={<Navigate to="/tasks" />} />
              <Route
                path="complex-loss"
                element={
                  <CanNavigateToPage
                    I="access"
                    can="complex-loss"
                    snackNotifications={snackNotifications}
                  >
                    <ComplexLossPage />
                  </CanNavigateToPage>
                }
              >
                <Route path=":taskId" element={<ComplexLossDetailsPage />} />
              </Route>
              <Route path="tasks" element={<TasksListPage />}>
                <Route
                  path={`add`}
                  element={
                    <CanNavigateToPage
                      I="create"
                      can="task"
                      snackNotifications={snackNotifications}
                    >
                      <TaskCreatePage />
                    </CanNavigateToPage>
                  }
                />
                <Route
                  path={`add-qualified`}
                  element={
                    <CanNavigateToPage
                      I="create"
                      can="task"
                      snackNotifications={snackNotifications}
                    >
                      <QualifiedResourcesTaskCreatePage />
                    </CanNavigateToPage>
                  }
                >
                  <Route path=":resourceId" element={<ResourceDetailsPage />} />
                </Route>
                <Route path=":taskId" element={<TaskDetailsPage />} />
                <Route
                  path={'resources/:resourceId'}
                  element={<ResourceDetailsPage navigateToOnClose={'/tasks'} />}
                />
                {/* this route is for the bulk cancel page */}
                <Route
                  path={`bulk-cancel`}
                  element={<BulkTaskCancellation />}
                />
                {/* this route is for the bulk lock page */}
                <Route path={`bulk-lock`} element={<BulkTaskLock />} />
              </Route>
              <Route path="scheduler" element={<SchedulerViewPage />}>
                <Route
                  path={`add`}
                  element={
                    <CanNavigateToPage
                      I="create"
                      can="task"
                      snackNotifications={snackNotifications}
                    >
                      <TaskCreatePage />
                    </CanNavigateToPage>
                  }
                />
                <Route
                  path={`add-qualified`}
                  element={
                    <CanNavigateToPage
                      I="create"
                      can="task"
                      snackNotifications={snackNotifications}
                    >
                      <QualifiedResourcesTaskCreatePage />
                    </CanNavigateToPage>
                  }
                >
                  <Route path=":resourceId" element={<ResourceDetailsPage />} />
                </Route>
                <Route
                  path=":taskId"
                  element={<TaskDetailsPage navigateToOnClose="/scheduler" />}
                />
                <Route
                  path={'resources/:resourceId'}
                  element={
                    <ResourceDetailsPage navigateToOnClose={'/scheduler'} />
                  }
                />
              </Route>
              <Route path="/reports" element={<ReportsPage />} />

              <Route path="admin">
                <Route
                  path="users"
                  element={
                    <CanNavigateToPage
                      I="put"
                      can="users"
                      snackNotifications={snackNotifications}
                    >
                      <UsersPage />
                    </CanNavigateToPage>
                  }
                >
                  <Route
                    path=":userId"
                    element={
                      <CanNavigateToPage
                        I="put"
                        can="users"
                        snackNotifications={snackNotifications}
                      >
                        <UserDetailsPage />
                      </CanNavigateToPage>
                    }
                  />
                  <Route
                    path={`add`}
                    element={
                      <CanNavigateToPage
                        I="put"
                        can="users"
                        snackNotifications={snackNotifications}
                      >
                        <UserCreatePage />
                      </CanNavigateToPage>
                    }
                  />
                </Route>
                <Route path="" element={<Navigate to="/admin/users" />} />
                <Route
                  path="task-templates"
                  element={
                    <CanNavigateToPage
                      I="set"
                      can="task-template"
                      snackNotifications={snackNotifications}
                    >
                      <TaskTemplatesPage />
                    </CanNavigateToPage>
                  }
                >
                  <Route
                    path=":taskTemplateId"
                    element={
                      <CanNavigateToPage
                        I="set"
                        can="task-template"
                        snackNotifications={snackNotifications}
                      >
                        <TaskTemplateDetailsPage />
                      </CanNavigateToPage>
                    }
                  />
                  <Route
                    path={`add`}
                    element={
                      <CanNavigateToPage
                        I="set"
                        can="task-template"
                        snackNotifications={snackNotifications}
                      >
                        <CreateTaskTemplate />
                      </CanNavigateToPage>
                    }
                  />
                </Route>
                <Route
                  path="regions"
                  element={
                    <CanNavigateToPage
                      I="set"
                      can="regions"
                      snackNotifications={snackNotifications}
                    >
                      <RegionsPage />
                    </CanNavigateToPage>
                  }
                >
                  <Route
                    path=":regionId"
                    element={
                      <CanNavigateToPage
                        I="set"
                        can="regions"
                        snackNotifications={snackNotifications}
                      >
                        <RegionsDetailsPage />
                      </CanNavigateToPage>
                    }
                  />
                  <Route
                    path={`add`}
                    element={
                      <CanNavigateToPage
                        I="set"
                        can="regions"
                        snackNotifications={snackNotifications}
                      >
                        <RegionsCreatePage />
                      </CanNavigateToPage>
                    }
                  />
                </Route>
                <Route
                  path="task-groups"
                  element={
                    <CanNavigateToPage
                      I="upsert"
                      can="task-group"
                      snackNotifications={snackNotifications}
                    >
                      <TaskGroupsPage />
                    </CanNavigateToPage>
                  }
                >
                  <Route
                    path=":taskGroupId"
                    element={
                      <CanNavigateToPage
                        I="upsert"
                        can="task-group"
                        snackNotifications={snackNotifications}
                      >
                        <TaskGroupDetailsPage />
                      </CanNavigateToPage>
                    }
                  />
                  <Route
                    path={`add`}
                    element={
                      <CanNavigateToPage
                        I="upsert"
                        can="task-group"
                        snackNotifications={snackNotifications}
                      >
                        <TaskGroupCreatePage />
                      </CanNavigateToPage>
                    }
                  />
                </Route>
                <Route
                  path="roles"
                  element={
                    <CanNavigateToPage
                      I="put"
                      can="roles"
                      snackNotifications={snackNotifications}
                    >
                      <RolesPage />
                    </CanNavigateToPage>
                  }
                >
                  <Route
                    path=":roleId"
                    element={
                      <CanNavigateToPage
                        I="put"
                        can="roles"
                        snackNotifications={snackNotifications}
                      >
                        <RoleDetailsPage />
                      </CanNavigateToPage>
                    }
                  />
                  <Route
                    path={`add`}
                    element={
                      <CanNavigateToPage
                        I="put"
                        can="roles"
                        snackNotifications={snackNotifications}
                      >
                        <RoleCreatePage />
                      </CanNavigateToPage>
                    }
                  />
                </Route>
                <Route
                  path="company-admin"
                  element={
                    <CanNavigateToPage
                      I="edit"
                      can="company-page"
                      snackNotifications={snackNotifications}
                    >
                      <CompanyAdminPage />
                    </CanNavigateToPage>
                  }
                />
              </Route>
              <Route
                path="resources"
                element={
                  <CanNavigateToPage
                    I="upsert"
                    can="resource"
                    snackNotifications={snackNotifications}
                    checkAdjuster={false}
                  >
                    <Resources />
                  </CanNavigateToPage>
                }
              >
                <Route path=":resourceId" element={<ResourceDetailsPage />} />
                <Route
                  path={`add`}
                  element={
                    <CanNavigateToPage
                      I="upsert"
                      can="resource"
                      snackNotifications={snackNotifications}
                    >
                      <ResourceCreatePage />
                    </CanNavigateToPage>
                  }
                />
                <Route
                  path={`update-exclusion-zones`}
                  element={
                    <CanNavigateToPage
                      I="upsert"
                      can="resource"
                      snackNotifications={snackNotifications}
                    >
                      <BulkUpdateResourcePage />
                    </CanNavigateToPage>
                  }
                />
              </Route>
              <Route
                path="capacity-dashboard"
                element={
                  <CanNavigateToPage
                    I="access"
                    can="capacity-dashboard"
                    snackNotifications={snackNotifications}
                  >
                    <CapacityDashboardPage />
                  </CanNavigateToPage>
                }
              />
            </Route>
          </Routes>
        </ExportNotificationProvider>
      </BrowserRouter>
    </AppProviders>
  </StrictMode>,
  document.getElementById('root')
);
